import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  Icon,
  Autocomplete,
  Alert,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import {
  StockAdjustmentFormData,
  AdjustmentType,
  ItemMaster,
  BatchInfo,
  Warehouse,
  Location,
  ADJUSTMENT_TYPE_OPTIONS,
} from "../../types/stock-adjustment.types";
import { stockAdjustmentService } from "../../services/stock-adjustment.service";

interface StockAdjustmentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: StockAdjustmentFormData) => void;
  initialData?: Partial<StockAdjustmentFormData>;
  title?: string;
  isLoading?: boolean;
  currentUser?: {
    id: string;
    name: string;
    role: string;
    isAdmin: boolean;
  };
}

const validationSchema = Yup.object({
  item_id: Yup.string().required("Item selection is required"),
  adjustment_type: Yup.string().required("Adjustment type is required"),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(0.01, "Quantity must be greater than 0")
    .test(
      "max-precision",
      "Quantity cannot have more than 3 decimal places",
      (value) => {
        if (value === undefined || value === null) return true;
        const str = value.toString();
        const decimalPart = str.split(".")[1];
        return !decimalPart || decimalPart.length <= 3;
      }
    ),
  warehouse_id: Yup.string().required("Warehouse selection is required"),
  batch_id: Yup.string().test(
    "batch-required",
    "Batch selection is required for batch-tracked items",
    function (value) {
      // This will be validated properly when selectedItem is available
      // For now, just allow empty values since we'll handle this in the component
      return true;
    }
  ),
  remarks: Yup.string().when("adjustment_type", {
    is: (val: string) => val === "manual" || val === "custom",
    then: () =>
      Yup.string().required(
        "Remarks are mandatory for manual or custom adjustments"
      ),
    otherwise: () => Yup.string(),
  }),
  custom_reason: Yup.string().when("adjustment_type", {
    is: "custom",
    then: () =>
      Yup.string().required(
        "Custom reason is required when adjustment type is custom"
      ),
    otherwise: () => Yup.string(),
  }),
  adjustment_date: Yup.number().required("Adjustment date is required"),
});

const defaultValues: StockAdjustmentFormData = {
  item_id: "",
  adjustment_type: "manual",
  quantity: 0,
  batch_id: "",
  warehouse_id: "",
  location_id: "",
  remarks: "",
  adjustment_date: Date.now(),
  custom_reason: "",
};

export const StockAdjustmentForm: React.FC<StockAdjustmentFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  title = "Stock Adjustment",
  isLoading = false,
  currentUser,
}) => {
  const [selectedItem, setSelectedItem] = React.useState<ItemMaster | null>(
    null
  );
  const [itemOptions, setItemOptions] = React.useState<ItemMaster[]>([]);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [batches, setBatches] = React.useState<BatchInfo[]>([]);
  const [batchesLoading, setBatchesLoading] = React.useState(false);
  const [warehouses, setWarehouses] = React.useState<Warehouse[]>([]);
  const [locations, setLocations] = React.useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

  const formik = useFormik({
    initialValues: { ...defaultValues, ...initialData },
    validationSchema,
    onSubmit: (values) => {
      console.log("Formik onSubmit called with values:", values);

      // Prepare submission data - only include batch_id if item is batch tracked
      const submissionData = { ...values };

      // If item is not batch tracked, don't include batch_id
      if (!selectedItem?.is_batch_tracked) {
        delete submissionData.batch_id;
      }

      // If location is empty, don't include location_id (make it optional)
      if (!values.location_id || values.location_id === "") {
        delete submissionData.location_id;
      }

      console.log("Calling parent onSubmit with data:", submissionData);
      onSubmit(submissionData);
    },
    enableReinitialize: true,
  });

  // Load warehouses on mount
  React.useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await stockAdjustmentService.listWarehouses();
        setWarehouses(response.data.data.data || []);
      } catch (error) {
        console.error("Error loading warehouses:", error);
        setWarehouses([]);
      }
    };

    if (open) {
      loadWarehouses();
    }
  }, [open]);

  // Load locations when warehouse changes
  React.useEffect(() => {
    const loadLocations = async () => {
      if (!formik.values.warehouse_id) {
        setLocations([]);
        return;
      }

      try {
        setLocationsLoading(true);
        const response = await stockAdjustmentService.getWarehouseLocations(
          formik.values.warehouse_id
        );
        setLocations(response.data.data.data || []);
      } catch (error) {
        console.error("Error loading locations:", error);
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    loadLocations();
  }, [formik.values.warehouse_id]);

  // Load batches when item changes
  React.useEffect(() => {
    const loadBatches = async () => {
      if (!selectedItem?.is_batch_tracked || !selectedItem?.id) {
        setBatches([]);
        return;
      }

      try {
        setBatchesLoading(true);
        const response = await stockAdjustmentService.getItemBatches(
          selectedItem.id
        );
        setBatches(response.data.data);
      } catch (error) {
        console.error("Error loading batches:", error);
      } finally {
        setBatchesLoading(false);
      }
    };

    loadBatches();
  }, [selectedItem]);

  // Search items
  const handleItemSearch = async (query: string) => {
    if (query.length < 2) {
      setItemOptions([]);
      return;
    }

    try {
      setItemsLoading(true);
      const response = await stockAdjustmentService.searchItemMaster(query);
      setItemOptions(response.data.data);
    } catch (error) {
      console.error("Error searching items:", error);
    } finally {
      setItemsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (item: ItemMaster | null) => {
    setSelectedItem(item);
    formik.setFieldValue("item_id", item?.id || "");
    formik.setFieldValue("batch_id", ""); // Reset batch selection
  };

  // Validate form
  const handleValidation = async () => {
    try {
      // Prepare validation data - only include batch_id if item is batch tracked
      const validationData = { ...formik.values };

      // If item is not batch tracked or no batch is selected, don't send batch_id
      if (!selectedItem?.is_batch_tracked || !formik.values.batch_id) {
        delete validationData.batch_id;
      }

      // If location is empty, don't send location_id (make it optional)
      if (!formik.values.location_id || formik.values.location_id === "") {
        delete validationData.location_id;
      }

      const response = await stockAdjustmentService.validateStockAdjustment(
        validationData
      );

      console.log("Full validation response:", response);
      console.log("Validation response data:", response.data);

      // Handle different response structures
      let validation: any;
      if ((response.data as any).data) {
        // Response structure: {success: true, data: {is_valid: true, errors: []}}
        validation = (response.data as any).data;
      } else {
        // Response structure: {is_valid: true, errors: []}
        validation = response.data;
      }

      console.log("Parsed validation:", validation);

      if (!validation.is_valid) {
        // Safely handle errors array
        const errors = validation.errors || [];
        const errorMessages = errors
          .map((e: any) => e.message || e)
          .filter(Boolean);
        setValidationErrors(
          errorMessages.length > 0 ? errorMessages : ["Validation failed"]
        );
        return false;
      }

      setValidationErrors([]);
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      setValidationErrors(["Validation failed. Please check your input."]);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("handleSubmit called");

    // Check if batch is required but not selected
    if (selectedItem?.is_batch_tracked && !formik.values.batch_id) {
      setValidationErrors([
        "Batch selection is required for batch-tracked items",
      ]);
      return;
    }

    console.log("Starting validation...");
    const isValid = await handleValidation();
    console.log("Validation result:", isValid);

    if (!isValid) return;

    console.log("Validation passed, calling formik.handleSubmit");
    formik.handleSubmit();
  };

  const handleClose = () => {
    formik.resetForm();
    setSelectedItem(null);
    setValidationErrors([]);
    onClose();
  };

  const isDateEditable = currentUser?.isAdmin || false;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "70vh" },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon>tune</Icon>
            {title}
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" component="div">
                  <strong>Please fix the following issues:</strong>
                  <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Typography>
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Item Selection */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Item Information
                  </Typography>

                  <Autocomplete
                    value={selectedItem}
                    onChange={(_, newValue) => handleItemSelect(newValue)}
                    onInputChange={(_, newInputValue) =>
                      handleItemSearch(newInputValue)
                    }
                    options={itemOptions}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.sku})`
                    }
                    loading={itemsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Item"
                        placeholder="Search by name or SKU..."
                        error={
                          !!(formik.touched.item_id && formik.errors.item_id)
                        }
                        helperText={
                          formik.touched.item_id && formik.errors.item_id
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {itemsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            SKU: {option.sku} | Current Stock:{" "}
                            {option.current_stock} {option.unit}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText="Type to search for items..."
                  />

                  {selectedItem && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Current Stock
                          </Typography>
                          <Typography variant="h6">
                            {selectedItem.current_stock} {selectedItem.unit}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Category
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.category}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Min Stock Level
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.min_stock_level} {selectedItem.unit}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Max Stock Level
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.max_stock_level} {selectedItem.unit}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Adjustment Details */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Adjustment Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={
                          !!(
                            formik.touched.adjustment_type &&
                            formik.errors.adjustment_type
                          )
                        }
                      >
                        <InputLabel>Adjustment Type</InputLabel>
                        <Select
                          name="adjustment_type"
                          value={formik.values.adjustment_type}
                          onChange={formik.handleChange}
                          label="Adjustment Type"
                        >
                          {ADJUSTMENT_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box>
                                <Typography variant="body1">
                                  {option.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {option.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.adjustment_type &&
                          formik.errors.adjustment_type && (
                            <FormHelperText>
                              {formik.errors.adjustment_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="quantity"
                        label="Quantity"
                        type="number"
                        value={formik.values.quantity}
                        onChange={formik.handleChange}
                        error={
                          !!(formik.touched.quantity && formik.errors.quantity)
                        }
                        helperText={
                          formik.touched.quantity && formik.errors.quantity
                        }
                        inputProps={{
                          min: 0,
                          step: 0.001,
                        }}
                        InputProps={{
                          endAdornment: selectedItem && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              {selectedItem.unit}
                            </Typography>
                          ),
                        }}
                      />
                    </Grid>

                    {formik.values.adjustment_type === "custom" && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="custom_reason"
                          label="Custom Reason"
                          value={formik.values.custom_reason}
                          onChange={formik.handleChange}
                          error={
                            !!(
                              formik.touched.custom_reason &&
                              formik.errors.custom_reason
                            )
                          }
                          helperText={
                            formik.touched.custom_reason &&
                            formik.errors.custom_reason
                          }
                          placeholder="Please specify the reason for this adjustment"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Batch Selection (if applicable) */}
              {selectedItem?.is_batch_tracked && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, bgcolor: "background.default" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mb: 2 }}
                    >
                      Batch Selection
                    </Typography>

                    {batchesLoading ? (
                      <Box
                        sx={{ display: "flex", justifyContent: "center", p: 2 }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel>Select Batch</InputLabel>
                        <Select
                          name="batch_id"
                          value={formik.values.batch_id}
                          onChange={formik.handleChange}
                          label="Select Batch"
                        >
                          {batches.map((batch) => (
                            <MenuItem key={batch.id} value={batch.id}>
                              <Box sx={{ width: "100%" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography variant="body1">
                                    {batch.batch_number}
                                  </Typography>
                                  <Chip
                                    label={`${batch.remaining_quantity} ${selectedItem.unit}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {batch.expiry_date &&
                                    `Expires: ${format(
                                      new Date(batch.expiry_date),
                                      "MMM dd, yyyy"
                                    )}`}
                                  {batch.expiry_date && " | "}
                                  Created:{" "}
                                  {format(
                                    new Date(batch.created_date),
                                    "MMM dd, yyyy"
                                  )}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Location Details */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Location Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={
                          !!(
                            formik.touched.warehouse_id &&
                            formik.errors.warehouse_id
                          )
                        }
                      >
                        <InputLabel>Warehouse</InputLabel>
                        <Select
                          name="warehouse_id"
                          value={formik.values.warehouse_id}
                          onChange={formik.handleChange}
                          label="Warehouse"
                        >
                          {warehouses.map((warehouse) => (
                            <MenuItem key={warehouse.id} value={warehouse.id}>
                              <Box>
                                <Typography variant="body1">
                                  {warehouse.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {warehouse.code}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.warehouse_id &&
                          formik.errors.warehouse_id && (
                            <FormHelperText>
                              {formik.errors.warehouse_id}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        disabled={
                          !formik.values.warehouse_id || locationsLoading
                        }
                      >
                        <InputLabel>Location (Optional)</InputLabel>
                        <Select
                          name="location_id"
                          value={formik.values.location_id}
                          onChange={formik.handleChange}
                          label="Location (Optional)"
                        >
                          <MenuItem value="">
                            <em>No specific location</em>
                          </MenuItem>
                          {locations.map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                              <Box>
                                <Typography variant="body1">
                                  {location.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {location.code}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Additional Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Adjustment Date"
                        value={new Date(formik.values.adjustment_date)}
                        onChange={(date) => {
                          formik.setFieldValue(
                            "adjustment_date",
                            date?.getTime() || Date.now()
                          );
                        }}
                        disabled={!isDateEditable}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={
                              !!(
                                formik.touched.adjustment_date &&
                                formik.errors.adjustment_date
                              )
                            }
                            helperText={
                              !isDateEditable
                                ? "Only admins can edit the adjustment date"
                                : formik.touched.adjustment_date &&
                                  formik.errors.adjustment_date
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Authorized By"
                        value={currentUser?.name || "Unknown User"}
                        disabled
                        helperText="Current user will be recorded as the authorizer"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="remarks"
                        label={
                          formik.values.adjustment_type === "manual" ||
                          formik.values.adjustment_type === "custom"
                            ? "Remarks (Required)"
                            : "Remarks (Optional)"
                        }
                        multiline
                        rows={3}
                        value={formik.values.remarks}
                        onChange={formik.handleChange}
                        error={
                          !!(formik.touched.remarks && formik.errors.remarks)
                        }
                        helperText={
                          formik.touched.remarks && formik.errors.remarks
                        }
                        placeholder="Enter detailed remarks about this adjustment..."
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !formik.isValid}
              startIcon={
                isLoading ? <CircularProgress size={16} /> : <Icon>save</Icon>
              }
            >
              {isLoading ? "Saving..." : "Save Adjustment"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};
